import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import { usePageView } from '../hooks/useAnalytics'
import './about.css'

const About = () => {
  const { t } = useTranslation('about')

  usePageView('About')

  return (
    <div className="about-container">
      <Helmet>
        <title>{t('meta.title')} - ORRAM Frames</title>
        <meta property="og:title" content="About ORRAM - Golden Ratio Picture Frames" />
        <meta name="description" content={t('meta.description')} />
      </Helmet>
      <Navigation />

      <main className="about-main">
        {/* Hero Section */}
        <section className="about-hero">
          <div className="about-hero-content">
            <h1 className="about-hero-title">ORRAM</h1>
            <p className="about-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="about-hero-description">
              {t('hero.description')}
            </p>
          </div>

          <div className="about-hero-image">
            <img src="/images/about-hero.jpg" alt="Beautiful ORRAM frame with artwork." />
          </div>
        </section>

        {/* The Story Section */}
        <section className="about-story">
          <div className="section-container">
            <h2 className="section-title">{t('story.title')}</h2>

            <div className="story-blocks">
              <div className="story-block">
                <h3>{t('story.beginning.title')}</h3>
                <p>{t('story.beginning.p1')}</p>
                <p>{t('story.beginning.p2')}</p>
                <p>{t('story.beginning.p3')}</p>
              </div>

              <div className="story-block">
                <h3>{t('story.encounter.title')}</h3>
                <blockquote className="story-quote">
                  "{t('story.encounter.quote')}"
                </blockquote>
                <p>{t('story.encounter.p1')}</p>
              </div>

              <div className="story-block">
                <h3>{t('story.birth.title')}</h3>
                <p>{t('story.birth.p1')}</p>
                <p>
                  {t('story.birth.p2.before')}
                  <strong className="gold-accent"> 17 </strong>
                  {t('story.birth.p2.after')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="about-philosophy">
          <div className="section-container">
            <h2 className="section-title">{t('philosophy.title')}</h2>

            <div className="about-philosophy-image">
              <img src="/images/about-philosophy.jpg" alt="Golden ratio visualization applied to ORRAM frame design." />
            </div>

            <div className="philosophy-grid">
              <div className="philosophy-item">
                <h3>{t('philosophy.renaissance.title')}</h3>
                <p>{t('philosophy.renaissance.description')}</p>
              </div>

              <div className="philosophy-item">
                <h3>{t('philosophy.contemporary.title')}</h3>
                <p>{t('philosophy.contemporary.description')}</p>
              </div>

              <div className="philosophy-item">
                <h3>{t('philosophy.unique.title')}</h3>
                <p>{t('philosophy.unique.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Craftsmanship Section */}
        <section className="about-craftsmanship">
          <div className="section-container">
            <h2 className="section-title">{t('craftsmanship.title')}</h2>

            <div className="craftsmanship-grid">
              <div className="craft-block">
                <h3>{t('craftsmanship.precision.title')}</h3>
                <ul className="craft-list">
                  <li>{t('craftsmanship.precision.item1')}</li>
                  <li>{t('craftsmanship.precision.item2')}</li>
                  <li>{t('craftsmanship.precision.item3')}</li>
                  <li>{t('craftsmanship.precision.item4')}</li>
                </ul>
              </div>

              <div className="about-craft-image">
                <img src="/images/about-workshop.png" alt="CNC machine cutting frame components with precision in ORRAM workshop." />
              </div>
            </div>

            <div className="craftsmanship-grid reverse">
              <div className="about-craft-image">
                <img src="/images/about-paint-booth.jpg" alt="Paint booth where ORRAM frames are finished with precision coating." />
              </div>

              <div className="craft-block">
                <h3>{t('craftsmanship.finishing.title')}</h3>
                <ul className="craft-list">
                  <li>{t('craftsmanship.finishing.item1')}</li>
                  <li>{t('craftsmanship.finishing.item2')}</li>
                  <li>{t('craftsmanship.finishing.item3')}</li>
                  <li>{t('craftsmanship.finishing.item4')}</li>
                </ul>
              </div>
            </div>

            <div className="craftsmanship-grid">
              <div className="craft-block">
                <h3>{t('craftsmanship.compatibility.title')}</h3>
                <ul className="craft-list">
                  <li>{t('craftsmanship.compatibility.item1')}</li>
                  <li>{t('craftsmanship.compatibility.item2')}</li>
                  <li>{t('craftsmanship.compatibility.item3')}</li>
                </ul>
              </div>

              <div className="about-craft-image about-craft-image--wide">
                <img src="/images/about-frames-collection.jpg" alt="Collection of finished ORRAM frames ready for delivery." />
              </div>
            </div>
          </div>
        </section>

        {/* Promise Section */}
        <section className="about-promise">
          <div className="section-container">
            <h2 className="section-title">{t('promise.title')}</h2>

            <div className="promise-items">
              <div className="promise-item">
                <p>{t('promise.dimensions')}</p>
              </div>

              <div className="promise-item">
                <p>{t('promise.finishes')}</p>
              </div>

              <div className="promise-item">
                <p>{t('promise.delivery')}</p>
              </div>

              <div className="promise-item">
                <p>{t('promise.timeline')}</p>
              </div>
            </div>

            <p className="promise-tagline">{t('promise.tagline')}</p>

            <div className="about-promise-image">
              <img src="/images/about-packaging.jpeg" alt="Premium ORRAM packaging designed to safely deliver frames." />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-cta">
          <div className="section-container">
            <h2 className="cta-title">{t('cta.title')}</h2>
            <Link to="/#calculatorSection" className="btn btn-primary btn-lg cta-button">
              {t('cta.button')}
            </Link>
            <p className="cta-contact">
              {t('cta.questions')} <Link to="/contact" className="cta-contact-link">{t('cta.contactUs')}</Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default About
